import { FadeUp } from "../components/Reveal";

const Section = ({ title, children, i }) => (
  <FadeUp delay={i * 0.04} className="border-t border-cream-border pt-8 mt-8 first:border-0 first:mt-0 first:pt-0" data-testid={`legal-section-${i}`}>
    <h2 className="font-serif text-2xl md:text-3xl text-ink mb-4">{title}</h2>
    <div className="space-y-4 text-ink-muted leading-relaxed">{children}</div>
  </FadeUp>
);

const Shell = ({ eyebrow, title, updated, children, testid }) => (
  <div data-testid={testid}>
    <section className="bg-cream pt-40 pb-16">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <FadeUp><p className="eyebrow mb-6">{eyebrow}</p></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-ink">{title}</h1>
          <p className="text-sm text-ink-muted mt-5">Last updated: {updated}</p>
        </FadeUp>
      </div>
    </section>
    <section className="bg-cream pb-28">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">{children}</div>
    </section>
  </div>
);

export function PrivacyPolicy() {
  return (
    <Shell eyebrow="Legal" title="Privacy Policy" updated="August 2026" testid="privacy-page">
      <Section i={0} title="Who we are">
        <p>
          The Scent Butler ("we", "us", "our") provides commercial scenting systems and fragrances
          to businesses across Australia. This policy explains what personal information we collect,
          how we use it, and the choices you have. We handle personal information in line with the
          Privacy Act 1988 (Cth) and the Australian Privacy Principles.
        </p>
      </Section>
      <Section i={1} title="What we collect">
        <p>We only collect what we need to respond to you and run our business. That typically includes:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Your name, business name, email, phone number and role.</li>
          <li>Details about your space and enquiry (space type, size, timeline, budget).</li>
          <li>Records of our correspondence, quotes, orders and servicing visits.</li>
          <li>Basic website analytics, such as pages visited and general location.</li>
        </ul>
      </Section>
      <Section i={2} title="How we use it">
        <p>
          We use your information to answer enquiries, prepare proposals, deliver and service your
          scenting system, send you information you've asked for, and meet our legal and safety
          obligations. We don't sell your personal information to anyone.
        </p>
      </Section>
      <Section i={3} title="Who we share it with">
        <p>
          We share information only where it's needed to provide our service — for example with our
          installation team, delivery partners, or software providers who help us operate (such as
          email and analytics tools). These providers are bound to keep your information secure and
          use it only for the purpose we've engaged them for.
        </p>
      </Section>
      <Section i={4} title="How we protect it">
        <p>
          We take reasonable steps to keep your information secure and to destroy or de-identify it
          once we no longer need it. No system is perfectly secure, but we work to reduce the risk of
          loss, misuse or unauthorised access.
        </p>
      </Section>
      <Section i={5} title="Your choices">
        <p>
          You can ask to see the personal information we hold about you, ask us to correct it, or ask
          us to stop sending you marketing. Just email us and we'll sort it out. If you have a
          concern about how we've handled your information, tell us first — we'd like the chance to
          put it right.
        </p>
      </Section>
      <Section i={6} title="Contact us">
        <p>
          Questions about this policy or your information? Email{" "}
          <a href="mailto:hello@thescentbutler.com.au" className="text-gold link-underline">hello@thescentbutler.com.au</a>{" "}
          or call 1300 123 456.
        </p>
      </Section>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell eyebrow="Legal" title="Terms & Conditions" updated="August 2026" testid="terms-page">
      <Section i={0} title="These terms">
        <p>
          These terms cover your use of our website and the products and services we provide. By
          enquiring with us or placing an order, you agree to them. If anything here isn't clear,
          get in touch before you go ahead.
        </p>
      </Section>
      <Section i={1} title="Quotes and orders">
        <p>
          Quotes are based on the information you give us about your space and are valid for 30 days
          unless we say otherwise. An order is confirmed once we've accepted it in writing and, where
          applicable, received any agreed deposit. Prices are in Australian dollars and exclude GST
          unless stated.
        </p>
      </Section>
      <Section i={2} title="Installation and servicing">
        <p>
          We'll agree a time to install and configure your system, and you're responsible for giving
          us safe, reasonable access to the site. Where you're on a refill or servicing plan, we'll
          keep your system topped up and maintained on the schedule we agree.
        </p>
      </Section>
      <Section i={3} title="Fragrances and safety">
        <p>
          Our fragrance oils are IFRA-compliant and intended for use in our diffusion equipment as
          directed. Safety Data Sheets are available on request. Please follow our guidance on
          placement and settings — misuse, or use with other equipment, is at your own risk.
        </p>
      </Section>
      <Section i={4} title="Payment">
        <p>
          Invoices are payable by the date shown. We may pause servicing or refill deliveries on
          overdue accounts. Hardware we supply on a rental or managed basis remains our property
          unless we've agreed a purchase in writing.
        </p>
      </Section>
      <Section i={5} title="Liability">
        <p>
          Nothing in these terms excludes rights you have under the Australian Consumer Law. To the
          extent the law allows, our liability is limited to re-supplying the product or service, or
          the cost of doing so. We're not liable for indirect or consequential loss.
        </p>
      </Section>
      <Section i={6} title="Changes and contact">
        <p>
          We may update these terms from time to time; the current version will always be on this
          page. Questions? Email{" "}
          <a href="mailto:hello@thescentbutler.com.au" className="text-gold link-underline">hello@thescentbutler.com.au</a>{" "}
          or call 1300 123 456.
        </p>
      </Section>
    </Shell>
  );
}
